import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useCategories, useCategoryTree } from '../hooks/useQueries';
import './Navbar.css';

// Below this width the nav becomes a vertical drawer (hamburger menu) —
// overflow/"More" logic only applies above this breakpoint. Keep in sync
// with the @media (max-width: 900px) rule in Navbar.css.
const MOBILE_BREAKPOINT = 900;

// Fixed spacing values pulled from Navbar.css — used to compute how much
// width is actually available for nav links between the logo and actions.
const INNER_GAP = 32;   // .navbar__inner { gap: 32px } — appears twice (logo↔nav, nav↔actions)
const INNER_PADDING = 48; // .navbar__inner { padding: 0 24px } — both sides

const NavbarComponent = () => {
  const { totalItems } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [openCat, setOpenCat] = useState(null); // id of expanded category submenu (mobile accordion / desktop click fallback)
  const [customer, setCustomer] = useState(() => {
    try { return JSON.parse(localStorage.getItem('customer')); } catch { return null; }
  });

  // "More" overflow menu state
  const [moreOpen, setMoreOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(Infinity);

  const dropdownRef = useRef(null);
  const moreRef = useRef(null);
  const innerRef = useRef(null);   // .navbar__inner — gives total available width
  const logoRef = useRef(null);
  const actionsRef = useRef(null);
  const measureRef = useRef(null); // hidden clone used to measure natural item widths

  const navigate = useNavigate();
  const location = useLocation();

  const { data: categories = [], isLoading: catsLoading } = useCategories();
  const currentCategory = new URLSearchParams(location.search).get('category') || '';
  const { data: categoryTree = [] } = useCategoryTree();

  // Re-read customer from localStorage whenever route changes (e.g. after login)
  useEffect(() => {
    try {
      const stored = localStorage.getItem('customer');
      setCustomer(stored ? JSON.parse(stored) : null);
    } catch {
      setCustomer(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Collapse any open category submenu on navigation or when the mobile menu closes
  useEffect(() => { setOpenCat(null); }, [location.pathname]);
  useEffect(() => { if (!menuOpen) setOpenCat(null); }, [menuOpen]);

  // Close account dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close "More" dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close "More" dropdown on route change
  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  // ── Overflow calculation ────────────────────────────────────────────────
  // Measures the real rendered width of every "Home / All Gifts / category"
  // link (via the hidden .navbar__measure clone) against the actual space
  // available in the header, and decides how many categories can render
  // before a "More" button is needed. Re-runs on window resize and whenever
  // the category list changes (e.g. after the API loads or admin adds one).
  useLayoutEffect(() => {
    function recalc() {
      // Below the breakpoint the nav is a vertical drawer — show everything,
      // no overflow menu needed.
      if (window.innerWidth <= MOBILE_BREAKPOINT) {
        setVisibleCount(categoryTree.length);
        return;
      }

      const inner = innerRef.current;
      const measure = measureRef.current;
      if (!inner || !measure || categoryTree.length === 0) {
        setVisibleCount(categoryTree.length);
        return;
      }

      const logoW = logoRef.current?.offsetWidth ?? 0;
      const actionsW = actionsRef.current?.offsetWidth ?? 0;
      const available = inner.offsetWidth - logoW - actionsW - INNER_GAP - INNER_PADDING;

      // measure children: [Home, All Gifts, ...categories, More]
      const measureChildren = Array.from(measure.children);
      if (measureChildren.length < categoryTree.length + 3) {
        // not fully rendered yet — try again next tick
        return;
      }
      const moreBtnWidth = measureChildren[measureChildren.length - 1].offsetWidth + 4;

      // Home + All Gifts are always shown
      let used = measureChildren[0].offsetWidth + 4 + measureChildren[1].offsetWidth + 4;
      let fitCount = categoryTree.length;

      for (let i = 0; i < categoryTree.length; i++) {
        const itemWidth = measureChildren[i + 2].offsetWidth + 4;
        const remaining = categoryTree.length - i - 1;
        const budget = available - (remaining > 0 ? moreBtnWidth : 0);
        if (used + itemWidth > budget) {
          fitCount = i;
          break;
        }
        used += itemWidth;
      }
      setVisibleCount(fitCount);
    }

    // Run after paint so refs/measure clone have real dimensions
    const raf = requestAnimationFrame(recalc);
    window.addEventListener('resize', recalc);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', recalc);
    };
  }, [categoryTree]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('customer');
    setCustomer(null);
    setDropdownOpen(false);
    navigate('/');
  };

  const isAllGiftsActive = location.pathname === '/products' && currentCategory === '';

  // True if this category node, or any of its nested subcategories/leaves, is the one currently selected
  const categoryIsActive = (node) => {
    if (!currentCategory) return false;
    if (node.slug === currentCategory) return true;
    return (node.children || []).some((child) => categoryIsActive(child));
  };

  // Initials avatar helper
  const getInitials = (c) => {
    if (!c) return null;
    return `${c.firstName?.[0] ?? ''}${c.lastName?.[0] ?? ''}`.toUpperCase() || '?';
  };

  // Split categories into what fits inline vs. what goes under "More"
  const visibleCats = categoryTree.slice(0, visibleCount);
  const overflowCats = categoryTree.slice(visibleCount);
  const overflowIsActive = overflowCats.some((cat) => categoryIsActive(cat));

  return (
    <>
      {/* Top banner */}
      {/* <div className="nav-banner">
        <p>🎁 Free shipping on orders above ₹999 &nbsp;·&nbsp; 🎀 Gift wrapping available</p>
      </div> */}

      <header className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`}>
        <div className="navbar__inner" ref={innerRef}>

          {/* Logo */}
          <Link to="/" className="navbar__logo" ref={logoRef}>
            <img
              src="https://res.cloudinary.com/dfl6zlu16/image/upload/v1784294408/Untitled_design_cnrpon.png"
              alt="Fcreation Logo"
              className="navbar__logo-image"
            />
          </Link>

          {/* Nav links */}
          <nav className={`navbar__nav ${menuOpen ? 'navbar__nav--open' : ''}`}>
            <NavLink
              to="/"
              end
              className={({ isActive }) => `navbar__link ${isActive ? 'navbar__link--active' : ''}`}
              onClick={() => setMenuOpen(false)}
            >
              Home
            </NavLink>

            <Link
              to="/products"
              className={`navbar__link ${isAllGiftsActive ? 'navbar__link--active' : ''}`}
              onClick={() => setMenuOpen(false)}
            >
              All Gifts
            </Link>

            {visibleCats.map((cat) => {
              const hasChildren = cat.children?.length > 0;
              const isOpen = openCat === cat.id;
              const isActive = categoryIsActive(cat);
              return (
                <div
                  key={cat.id}
                  className={`navbar__dropdown-parent ${isOpen ? 'navbar__dropdown-parent--open' : ''}`}
                >
                  <div className="navbar__link-row">
                    <Link
                      to={`/products?category=${cat.slug}`}
                      className={`navbar__link ${hasChildren ? 'navbar__link--dropdown' : ''} ${isActive ? 'navbar__link--active' : ''}`}
                      onClick={() => setMenuOpen(false)}
                    >
                      {cat.name}
                      {hasChildren && (
                        <svg className="navbar__link-caret" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      )}
                    </Link>
                    {hasChildren && (
                      <button
                        type="button"
                        className="navbar__flyout-toggle"
                        aria-label={`Toggle ${cat.name} submenu`}
                        aria-expanded={isOpen}
                        onClick={(e) => { e.preventDefault(); setOpenCat(isOpen ? null : cat.id); }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {hasChildren && (
                    <div className="navbar__flyout">
                      {cat.children.map((sub) => (
                        <div key={sub.id} className="navbar__flyout-col">
                          <Link
                            to={`/products?category=${sub.slug}`}
                            className={`navbar__flyout-item navbar__flyout-item--bold ${currentCategory === sub.slug ? 'navbar__flyout-item--active' : ''}`}
                            onClick={() => { setMenuOpen(false); setOpenCat(null); }}
                          >
                            {sub.name}
                          </Link>
                          {sub.children?.map((leaf) => (
                            <Link
                              key={leaf.id}
                              to={`/products?category=${leaf.slug}`}
                              className={`navbar__flyout-item ${currentCategory === leaf.slug ? 'navbar__flyout-item--active' : ''}`}
                              onClick={() => { setMenuOpen(false); setOpenCat(null); }}
                            >
                              {leaf.name}
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* "More" — only rendered when categories don't all fit inline.
                On mobile (drawer view) visibleCount === categoryTree.length,
                so overflowCats is always empty and this never renders there. */}
            {overflowCats.length > 0 && (
              <div
                className={`navbar__more-wrap ${moreOpen ? 'navbar__more-wrap--open' : ''}`}
                ref={moreRef}
              >
                {/* Opens on hover via CSS (matches the category flyouts above).
                    The click handler + --open class is a fallback for touch
                    devices wide enough to hit this breakpoint (e.g. tablets
                    in landscape) that don't support :hover. */}
                <button
                  type="button"
                  className={`navbar__more-btn ${moreOpen ? 'navbar__more-btn--open' : ''} ${overflowIsActive ? 'navbar__link--active' : ''}`}
                  aria-expanded={moreOpen}
                  onClick={() => setMoreOpen((o) => !o)}
                >
                  More
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>

                {/* Always mounted (not conditionally rendered) so CSS :hover
                    can reveal it — visibility is controlled entirely by
                    .navbar__more-wrap:hover / .navbar__more-wrap--open in
                    Navbar.css, same pattern as .navbar__flyout. */}
                <div className="navbar__more-dropdown">
                  {overflowCats.map((cat) => (
                    <React.Fragment key={cat.id}>
                      <Link
                        to={`/products?category=${cat.slug}`}
                        className={`navbar__more-item ${currentCategory === cat.slug ? 'navbar__more-item--active' : ''}`}
                        onClick={() => { setMoreOpen(false); setMenuOpen(false); }}
                      >
                        {cat.name}
                      </Link>
                      {cat.children?.map((sub) => (
                        <Link
                          key={sub.id}
                          to={`/products?category=${sub.slug}`}
                          className={`navbar__more-subitem ${currentCategory === sub.slug ? 'navbar__more-item--active' : ''}`}
                          onClick={() => { setMoreOpen(false); setMenuOpen(false); }}
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </nav>

          {/* Actions */}
          <div className="navbar__actions" ref={actionsRef}>

            {/* Search */}
            <button className="navbar__icon-btn navbar__avatar-btn" onClick={() => setSearchOpen(true)} aria-label="Search">
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
              </svg>
            </button>

            {/* User avatar / dropdown */}
            <div className="navbar__user-wrap" ref={dropdownRef}>
              <button
                className="navbar__icon-btn navbar__avatar-btn"
                onClick={() => customer ? setDropdownOpen(o => !o) : navigate('/login')}
                aria-label="My Account"
              >
                {customer ? (
                  <span className="navbar__avatar-initials">{getInitials(customer)}</span>
                ) : (
                  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                  </svg>
                )}
              </button>

              {/* Dropdown */}
              {customer && dropdownOpen && (
                <div className="navbar__dropdown">
                  {/* User info header */}
                  <div className="navbar__dropdown-header">
                    <div className="navbar__dropdown-avatar">{getInitials(customer)}</div>
                    <div>
                      <p className="navbar__dropdown-name">
                        {customer.firstName} {customer.lastName}
                      </p>
                      <p className="navbar__dropdown-email">{customer.email}</p>
                    </div>
                  </div>

                  <div className="navbar__dropdown-divider" />

                  {/* Menu items */}
                  <Link
                    to="/account"
                    className="navbar__dropdown-item"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="12" cy="8" r="4" />
                      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                    </svg>
                    My Account
                  </Link>


                  <Link
                    to="/account"
                    state={{ tab: 'Orders history' }}
                    className="navbar__dropdown-item"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M5 8h14M5 8a2 2 0 1 0 0-4h14a2 2 0 1 0 0 4M5 8v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8m-9 4h4" />
                    </svg>
                    My Orders
                  </Link>

                  <div className="navbar__dropdown-divider" />

                  <button className="navbar__dropdown-item navbar__dropdown-logout" onClick={handleLogout}>
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
                    </svg>
                    Log out
                  </button>
                </div>
              )}
            </div>

            {/* Cart */}
            <Link to="/cart" className="navbar__cart-btn" aria-label="Cart">
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
              {totalItems > 0 && <span className="navbar__cart-badge">{totalItems}</span>}
            </Link>

            <button
              className={`navbar__hamburger ${menuOpen ? 'open' : ''}`}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <span /><span /><span />
            </button>
          </div>
        </div>

        {/* Hidden measuring clone — mirrors Home + All Gifts + every category
            + a "More" button, purely to read real rendered widths via
            offsetWidth. Never visible, never affects real layout. */}
        <div className="navbar__measure" ref={measureRef} aria-hidden="true">
          <span className="navbar__link">Home</span>
          <span className="navbar__link">All Gifts</span>
          {categoryTree.map((cat) => (
            <span className="navbar__link navbar__link--dropdown" key={cat.id}>
              {cat.name}
              {cat.children?.length > 0 && (
                <svg className="navbar__link-caret" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              )}
            </span>
          ))}
          <span className="navbar__more-btn">
            More
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </span>
        </div>
      </header>

      {/* Search Overlay */}
      {searchOpen && (
        <div className="search-overlay" onClick={() => setSearchOpen(false)}>
          <div className="search-modal" onClick={e => e.stopPropagation()}>
            <button className="search-close" onClick={() => setSearchOpen(false)}>✕</button>
            <p className="search-label">What are you looking for?</p>
            <form onSubmit={handleSearch}>
              <input
                autoFocus
                type="text"
                className="search-input"
                placeholder="Search gifts, occasions, categories..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </form>
            <div className="search-suggestions">
              {categories.slice(0, 4).map((cat) => (
                <button
                  key={cat.id}
                  className="search-tag"
                  onClick={() => {
                    navigate(`/products?category=${cat.slug}`);
                    setSearchOpen(false);
                  }}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {menuOpen && <div className="nav-backdrop" onClick={() => setMenuOpen(false)} />}
    </>
  );
};

export default NavbarComponent;